import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import {
  useMutationCancelOrganizationPlan,
  useMutationConfirmPlanSubscription,
  useMutationCreatePlanSubscription,
  useMutationResumeOrganizationPlan,
  useQueryGetCards,
  useQueryGetOrganizationSubscriptions
} from '../../../../../../queries/paymentQueries'
import { useQueryGetUserMe } from '../../../../../../queries/userQueries'
import { usePlanPermissions } from '../../../../../../hooks/usePlanPermissions'
import { ToastContext } from '../../../../../../context/ToastContext'
import { Modal } from '../../../../ui/Modal'
import CustomButton from '../../../../ui/CustomButton'
import ContractAddonsModal from '../../../../ui/ContractAddonsModal'
import PaymentForm from '../../../../payment/PaymentForm'
import AddCardPaymentForm from '../../../../payment/AddCardPaymentForm'
import PlanSelectionContent from './PlanSelectionContent'
import PlanCardSelectionContent from './PlanCardSelectionContent'
import PlanChangeConfirmContent from './PlanChangeConfirmContent'
import ResumePlanConfirmContent from './ResumePlanConfirmContent'
import SubscriptionHistoryTable from './SubscriptionHistoryTable'
import CurrentPlanSection from './CurrentPlanSection'
import CancelPlanModal from './CancelPlanModal'
import PlanReductionModal from './PlanReductionModal'
import ResizableModal from '../../../../ui/ResizableModal'
import { VALIDATION_AMOUNT } from '../../../../../utils/constant'
import { refreshTryLuca } from '../../../../../../utils/refreshTryLuca'

const CHANGE_PLAN_SLIDES = {
  plan: 0,
  card: 1,
  confirm: 2
}

const CHANGE_PLAN_FLOW_STEPS = [
  { label: 'Plan', slide: CHANGE_PLAN_SLIDES.plan },
  { label: 'Medio de pago', slide: CHANGE_PLAN_SLIDES.card },
  { label: 'Confirmacion', slide: CHANGE_PLAN_SLIDES.confirm }
]

const planMatchesFilter = (plan, filterPlanBy) => {
  if (!filterPlanBy) return true

  if (filterPlanBy.permission) {
    return plan[filterPlanBy.permission] === true
  }

  if (filterPlanBy.support && plan.allowedSupports) {
    return plan.allowedSupports.includes(filterPlanBy.support)
  }

  if (filterPlanBy.report && plan.allowedReports) {
    return plan.allowedReports.includes(filterPlanBy.report)
  }

  if (filterPlanBy.dashboard && plan.allowedDashboards) {
    return plan.allowedDashboards.includes(filterPlanBy.dashboard)
  }

  return true
}

const getPaymentErrorMessage = error => {
  if (!error) return 'No se pudo completar el cambio de plan. Intenta de nuevo.'

  const data = error?.response?.data

  if (data != null && typeof data === 'object' && typeof data.message === 'string') {
    return data.message
  }

  if (typeof data === 'string') return data
  if (typeof error?.message === 'string') return error.message

  return 'No se pudo completar el cambio de plan. Intenta de nuevo.'
}

const normalizePaymentTypeId = value => {
  if (value == null) return null

  const normalizedValue = String(value).trim().toLowerCase()

  if (!normalizedValue) return null
  if (normalizedValue.includes('credit')) return 'credit_card'
  if (normalizedValue.includes('debit')) return 'debit_card'

  return normalizedValue
}

const getRequestedPlanPaymentTypeId = plan => {
  const rawPaymentTypeId =
    plan?.paymentTypeId ??
    plan?.paymentType?.id ??
    plan?.paymentType?.paymentTypeId ??
    plan?.payment_type_id

  if (rawPaymentTypeId == null) return null

  const normalizedPaymentTypeId = normalizePaymentTypeId(rawPaymentTypeId)

  if (normalizedPaymentTypeId === 'credit_card') return 'credit_card'
  if (normalizedPaymentTypeId === 'debit_card') return 'debit_card'

  return null
}

const getActiveAddonQuantity = addonSub => {
  const addonType =
    addonSub?.addon?.type ??
    addonSub?.addon?.addonType ??
    addonSub?.type ??
    'number'

  if (addonType === 'bool') {
    return addonSub?.extraIntelligentBilling || addonSub?.extraPaymentMethods ? 1 : 1
  }

  const numericCandidates = [
    addonSub?.extraBusinessesAmount,
    addonSub?.extraBanksAmount
  ]

  const resolvedQuantity = numericCandidates.find(
    value => Number.isFinite(Number(value)) && Number(value) > 0
  )

  return resolvedQuantity != null ? Number(resolvedQuantity) : 1
}

const getCardPaymentTypeId = card => {
  const rawPaymentTypeId =
    card?.paymentTypeId ??
    card?.paymentType?.id ??
    card?.paymentType?.paymentTypeId ??
    card?.payment_type_id

  const normalizedPaymentTypeId = normalizePaymentTypeId(rawPaymentTypeId)

  if (normalizedPaymentTypeId === 'credit_card') return 'credit_card'
  if (normalizedPaymentTypeId === 'debit_card') return 'debit_card'

  return null
}

const filterCardsByPaymentType = (cards = [], paymentTypeId = null) => {
  if (!paymentTypeId) return cards

  return cards.filter(card => getCardPaymentTypeId(card) === paymentTypeId)
}

const getPreferredPlanChangeCardId = (
  filteredCards = [],
  activeSubscriptionCardId = '',
  pendingCardId = ''
) => {
  if (pendingCardId) return pendingCardId

  if (
    activeSubscriptionCardId &&
    filteredCards.some(card => String(card.id) === String(activeSubscriptionCardId))
  ) {
    return String(activeSubscriptionCardId)
  }

  if (filteredCards[0]?.id != null) {
    return String(filteredCards[0].id)
  }

  return ''
}

const getSubscriptionTimestamp = subscription =>
  new Date(subscription?.updatedAt ?? subscription?.startDate ?? subscription?.createdAt ?? 0).getTime()

const getActiveCurrentSubscription = (subscriptions = [], currentPlanId = null) => {
  const activeSubscriptions = (subscriptions || [])
    .filter(subscription => subscription?.isActive)
    .sort((left, right) => getSubscriptionTimestamp(right) - getSubscriptionTimestamp(left))

  if (currentPlanId != null) {
    const currentPlanSubscription = activeSubscriptions.find(
      subscription => Number(subscription?.planId) === Number(currentPlanId)
    )

    if (currentPlanSubscription) return currentPlanSubscription
  }

  return activeSubscriptions[0] || null
}

const handleTryLucaPlanRefresh = (callback, options = {}) => {
  callback?.()
  refreshTryLuca(options)
}

const PlansManager = ({
  isModal = false,
  isOpen = true,
  onClose,
  onPlanUpdated,
  showAddonsButton = false,
  filterPlanBy = null
}) => {
  const [showAddonsModal, setShowAddonsModal] = useState(false)
  const [showChangePlanModal, setShowChangePlanModal] = useState(false)
  const [showPlanReductionModal, setShowPlanReductionModal] = useState(false)
  const [showCancelPlanModal, setShowCancelPlanModal] = useState(false)
  const [showCancelPlanSuccessModal, setShowCancelPlanSuccessModal] = useState(false)
  const [showPlanChangeSuccessModal, setShowPlanChangeSuccessModal] = useState(false)
  const [showPaymentPendingReviewModal, setShowPaymentPendingReviewModal] = useState(false)
  const [showResumePlanModal, setShowResumePlanModal] = useState(false)
  const [showResumeAddCardModal, setShowResumeAddCardModal] = useState(false)
  const [resumeErrorMessage, setResumeErrorMessage] = useState('')
  const [resumeSelectedCardId, setResumeSelectedCardId] = useState('')
  const [isSavingResumeCard, setIsSavingResumeCard] = useState(false)
  const [resumePendingAddedCard, setResumePendingAddedCard] = useState(null)
  const [selectedPlanId, setSelectedPlanId] = useState(null)
  const [selectedCardId, setSelectedCardId] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [changePlanStep, setChangePlanStep] = useState(1)
  const [subscriptionId, setSubscriptionId] = useState(null)
  const [selectedFrequency, setSelectedFrequency] = useState('monthly')
  const [showAllPlans, setShowAllPlans] = useState(false)
  const [activeChangePlanSlide, setActiveChangePlanSlide] = useState(CHANGE_PLAN_SLIDES.plan)
  const [isSavingCard, setIsSavingCard] = useState(false)
  const [pendingAddedCard, setPendingAddedCard] = useState(null)
  const [didSkipCardSelection, setDidSkipCardSelection] = useState(false)
  const changePlanSwiperRef = useRef(null)

  const { data: userMe } = useQueryGetUserMe()
  const { canContractAddons } = usePlanPermissions()
  const organizationId = userMe?.currentOrganizationId || null

  const {
    data: {
      plans = [],
      subscriptions = [],
      addonSubscriptions = [],
      currentPlan = null,
      ufValue = 0
    } = { plans: [], subscriptions: [], addonSubscriptions: [], currentPlan: null, ufValue: 0 },
    isLoading
  } = useQueryGetOrganizationSubscriptions(organizationId)


  const activeAddons = useMemo(
    () => (addonSubscriptions || []).filter(addon => addon.isActive === true),
    [addonSubscriptions]
  )
  const activeAddonsSummary = useMemo(() => {
    const groupedAddons = new Map()

    activeAddons.forEach(addonSub => {
      const addonId = addonSub?.addonId ?? addonSub?.addon?.id ?? addonSub?.id
      const addonName = addonSub?.addon?.name ?? `Addon #${addonId}`
      const addonUf =
        addonSub?.addon?.ufPriceWithoutTaxes != null
          ? Number(addonSub.addon.ufPriceWithoutTaxes)
          : null
      const addonQuantity = getActiveAddonQuantity(addonSub)
      const addonTotalUf = addonUf != null ? addonUf * addonQuantity : null
      const currentAddon = groupedAddons.get(addonId)

      if (currentAddon) {
        currentAddon.quantity += addonQuantity
        currentAddon.totalUf =
          addonTotalUf != null
            ? (currentAddon.totalUf ?? 0) + addonTotalUf
            : currentAddon.totalUf
        return
      }

      groupedAddons.set(addonId, {
        addonId,
        name: addonName,
        quantity: addonQuantity,
        unitUf: addonUf,
        totalUf: addonTotalUf
      })
    })

    return Array.from(groupedAddons.values()).sort((left, right) =>
      left.name.localeCompare(right.name, 'es', { sensitivity: 'base' })
    )
  }, [activeAddons])

  const { data: cardsData, isLoading: cardsLoading } = useQueryGetCards()
  const cards = cardsData?.cards || []
  const customerId = cardsData?.customerId || null
  const pendingAddedCardId = pendingAddedCard?.id != null ? String(pendingAddedCard.id) : ''
  const visibleCards = useMemo(() => {
    if (!pendingAddedCardId) return cards

    return cards.filter(card => String(card.id) !== pendingAddedCardId)
  }, [cards, pendingAddedCardId])
  const resumePendingAddedCardId =
    resumePendingAddedCard?.id != null ? String(resumePendingAddedCard.id) : ''
  const resumeVisibleCards = useMemo(() => {
    if (!resumePendingAddedCardId) return cards

    const pendingCardAlreadyExists = cards.some(
      card => String(card.id) === String(resumePendingAddedCardId)
    )

    if (pendingCardAlreadyExists) return cards

    return [resumePendingAddedCard, ...cards]
  }, [cards, resumePendingAddedCard, resumePendingAddedCardId])
  const resumeVisibleCardsIds = useMemo(
    () =>
      (resumeVisibleCards || [])
        .map(card => card?.mercadopagoCardId)
        .filter(id => typeof id === 'string' && id.trim() !== ''),
    [resumeVisibleCards]
  )
  const resumePaymentCardsIds = useMemo(() => {
    if (!resumeSelectedCardId || resumeVisibleCards.length === 0) return resumeVisibleCardsIds

    const selectedCard = resumeVisibleCards.find(
      card => String(card.id) === String(resumeSelectedCardId)
    )
    const mercadoPagoCardId = selectedCard?.mercadopagoCardId

    if (typeof mercadoPagoCardId === 'string' && mercadoPagoCardId.trim() !== '') {
      return [mercadoPagoCardId.trim()]
    }

    return resumeVisibleCardsIds
  }, [resumeSelectedCardId, resumeVisibleCards, resumeVisibleCardsIds])

  const createPlanSubscription = useMutationCreatePlanSubscription()
  const confirmPlanSubscription = useMutationConfirmPlanSubscription()
  const cancelOrganizationPlan = useMutationCancelOrganizationPlan()
  const resumeOrganizationPlan = useMutationResumeOrganizationPlan()
  const { showToast } = useContext(ToastContext)

  const hasCurrentPlan = Boolean(currentPlan)
  const planIsCancelled = currentPlan?.isCancelled === true

  const availablePlans = useMemo(() => {
    let filtered = plans

    if (currentPlan) {
      filtered = plans.filter(plan => plan.id !== currentPlan.id)
    }

    filtered = filtered.filter(plan => plan.frequency === selectedFrequency)

    const hasActiveFilter =
      filterPlanBy &&
      (filterPlanBy.permission ||
        filterPlanBy.support ||
        filterPlanBy.report ||
        filterPlanBy.dashboard)

    if (hasActiveFilter && !showAllPlans) {
      filtered = filtered.filter(plan => planMatchesFilter(plan, filterPlanBy))
    }

    return filtered
  }, [plans, currentPlan, selectedFrequency, filterPlanBy, showAllPlans])

  const selectedPlan = plans.find(plan => plan.id === selectedPlanId)
  const requestedPlanPaymentTypeId = useMemo(
    () => getRequestedPlanPaymentTypeId(selectedPlan),
    [selectedPlan]
  )
  const resumePlanPaymentTypeId = useMemo(
    () => getRequestedPlanPaymentTypeId(currentPlan),
    [currentPlan]
  )
  const activeCurrentSubscription = useMemo(
    () => getActiveCurrentSubscription(subscriptions, currentPlan?.id),
    [currentPlan?.id, subscriptions]
  )
  const activeCurrentSubscriptionCardId =
    activeCurrentSubscription?.cardId != null ? String(activeCurrentSubscription.cardId) : ''
  const isPendingAddedCardSelected =
    pendingAddedCardId !== '' && String(selectedCardId) === pendingAddedCardId
  const filteredVisibleCards = useMemo(
    () => filterCardsByPaymentType(visibleCards, requestedPlanPaymentTypeId),
    [visibleCards, requestedPlanPaymentTypeId]
  )
  const preferredPlanChangeCardId = useMemo(
    () =>
      getPreferredPlanChangeCardId(
        filteredVisibleCards,
        activeCurrentSubscriptionCardId,
        pendingAddedCardId
      ),
    [activeCurrentSubscriptionCardId, filteredVisibleCards, pendingAddedCardId]
  )
  const filteredPlanPaymentCardsIds = useMemo(
    () =>
      filterCardsByPaymentType(cards, requestedPlanPaymentTypeId)
        .map(card => card.mercadopagoCardId)
        .filter(id => typeof id === 'string' && id.trim() !== ''),
    [cards, requestedPlanPaymentTypeId]
  )
  const selectedPaymentCard = useMemo(
    () => {
      if (isPendingAddedCardSelected) {
        return (
          cards.find(card => String(card.id) === pendingAddedCardId) || pendingAddedCard || null
        )
      }

      return filteredVisibleCards.find(card => String(card.id) === String(selectedCardId)) || null
    },
    [
      cards,
      filteredVisibleCards,
      isPendingAddedCardSelected,
      pendingAddedCard,
      pendingAddedCardId,
      selectedCardId
    ]
  )
  const selectedPlanPaymentCardsIds = useMemo(() => {
    const mercadoPagoCardId = selectedPaymentCard?.mercadopagoCardId

    if (typeof mercadoPagoCardId === 'string' && mercadoPagoCardId.trim() !== '') {
      return [mercadoPagoCardId.trim()]
    }

    return filteredPlanPaymentCardsIds
  }, [filteredPlanPaymentCardsIds, selectedPaymentCard])
  const selectedPlanPaymentTypeId = selectedPaymentCard?.paymentTypeId ?? requestedPlanPaymentTypeId
  const hasPlanFilter =
    filterPlanBy &&
    (filterPlanBy.permission ||
      filterPlanBy.support ||
      filterPlanBy.report ||
      filterPlanBy.dashboard)
  const showFilteredPlans = hasPlanFilter && !showAllPlans
  const isPending = createPlanSubscription.isPending || confirmPlanSubscription.isPending
  const isResumePending = resumeOrganizationPlan.isPending
  const isFlowBusy = isPending || isSavingCard
  const changePlanModalOpen = isModal ? isOpen : showChangePlanModal
  const selectionModalTitle = hasCurrentPlan ? 'Cambiar plan' : 'Contratar plan'
  const selectionModalDescription = hasCurrentPlan
    ? 'Selecciona el plan que quieres contratar. Los planes se adaptan al volumen de movimientos mensuales de tu empresa. Por eso, solo verás opciones que cubren correctamente tus necesidades.'
    : 'Selecciona el plan que quieres contratar. Los planes se adaptan al volumen de movimientos mensuales de tu empresa. Por eso, solo verás opciones que cubren correctamente tus necesidades. '
  const confirmModalTitle = hasCurrentPlan
    ? 'Confirmar cambio de plan'
    : 'Confirmar y validar medio de pago'
  const successModalTitle = hasCurrentPlan
    ? 'Plan actualizado correctamente'
    : 'Plan contratado correctamente'
  const successModalDescription = hasCurrentPlan
    ? 'Tu cambio de plan se ha realizado con exito.'
    : 'La contratacion del plan se ha realizado con exito.'

  const activeSlideMeta = {
    [CHANGE_PLAN_SLIDES.plan]: {
      title: selectionModalTitle,
      description: selectionModalDescription
    },
    [CHANGE_PLAN_SLIDES.card]: {
      title: 'Agregar medio de pago',
      description: 'Agrega tu tarjeta para activar tu plan.'
    },
    [CHANGE_PLAN_SLIDES.confirm]: {
      title: confirmModalTitle,
      description:
        'Por seguridad, necesitamos que ingreses nuevamente los datos de tu tarjeta antes de realizar el cobro del plan elegido.'
    }
  }[activeChangePlanSlide] ?? {
    title: selectionModalTitle,
    description: selectionModalDescription
  }
  const totalChangePlanSteps = CHANGE_PLAN_FLOW_STEPS.length
  const currentChangePlanStep = activeChangePlanSlide + 1

  const getChangePlanSwiper = () => {
    const swiper = changePlanSwiperRef.current

    if (!swiper || swiper.destroyed || !swiper.params) {
      return null
    }

    return swiper
  }

  const goToChangePlanSlide = slide => {
    getChangePlanSwiper()?.slideTo(slide)
    setActiveChangePlanSlide(slide)
    setErrorMessage('')
  }

  const updateChangePlanSwiperLayout = () => {
    requestAnimationFrame(() => {
      const swiper = getChangePlanSwiper()

      if (!swiper) return

      swiper.updateAutoHeight?.(0)
      swiper.update?.()
    })
  }

  const resetPlanFlowState = () => {
    setSelectedPlanId(null)
    setSelectedCardId('')
    setErrorMessage('')
    setChangePlanStep(1)
    setSubscriptionId(null)
    setShowChangePlanModal(false)
    setShowAllPlans(false)
    setIsSavingCard(false)
    setPendingAddedCard(null)
    setDidSkipCardSelection(false)
    setActiveChangePlanSlide(CHANGE_PLAN_SLIDES.plan)
    getChangePlanSwiper()?.slideTo(CHANGE_PLAN_SLIDES.plan, 0)
  }

  const handleChangePlanClick = () => {
    setSelectedPlanId(null)
    setSelectedCardId('')
    setErrorMessage('')
    setChangePlanStep(1)
    setSubscriptionId(null)
    setShowAllPlans(false)
    setIsSavingCard(false)
    setPendingAddedCard(null)
    setDidSkipCardSelection(false)
    setActiveChangePlanSlide(CHANGE_PLAN_SLIDES.plan)

    if (isModal) {
      onClose?.()
    } else {
      setShowChangePlanModal(true)
    }
  }

  const handleResumePlanClick = () => {
    setResumeErrorMessage('')
    setResumeSelectedCardId('')

    if (resumeVisibleCards.length === 0) {
      setShowResumeAddCardModal(true)
      return
    }

    setResumeSelectedCardId(String(resumeVisibleCards[0].id))
    setShowResumePlanModal(true)
  }

  const handleCloseResumeModal = () => {
    if (isResumePending || isSavingResumeCard) return

    setShowResumePlanModal(false)
    setResumeErrorMessage('')
    setResumeSelectedCardId('')
  }

  const handleCloseResumeAddCardModal = () => {
    if (isSavingResumeCard) return

    setShowResumeAddCardModal(false)
  }

  const handleResumeAddCardSuccess = card => {
    const nextCardId = card?.id != null ? String(card.id) : ''

    if (nextCardId) {
      setResumePendingAddedCard(card)
      setResumeSelectedCardId(nextCardId)
    }

    setResumeErrorMessage('')
    setShowResumeAddCardModal(false)
    setShowResumePlanModal(true)
    showToast('success', 'Tarjeta registrada correctamente')
  }

  const handleResumePaymentSuccess = async payload => {
    if (!payload?.token) {
      showToast('error', 'No se pudo obtener el token de preaprobacion')
      return
    }

    if (!organizationId || !currentPlan) {
      showToast('error', 'No se puede retomar el plan en este momento')
      throw new Error('Missing context')
    }

    const cardId = Number(resumeSelectedCardId)

    if (!resumeSelectedCardId || Number.isNaN(cardId) || cardId <= 0) {
      showToast('error', 'Selecciona una tarjeta')
      return
    }

    const body = {
      cardId,
      token: payload.token,
      ...(payload.preapprovalToken ? { preapprovalToken: payload.preapprovalToken } : {}),
      ...(payload.flowId ? { flowId: payload.flowId } : {}),
      ...(payload.payerEmail ? { payerEmail: payload.payerEmail } : {})
    }

    try {
      await resumeOrganizationPlan.mutateAsync({ organizationId, body })
      setShowResumePlanModal(false)
      setResumeErrorMessage('')
      setResumeSelectedCardId('')
      setResumePendingAddedCard(null)
      showToast('success', 'Plan retomado correctamente')
      handleTryLucaPlanRefresh(onPlanUpdated, { delayMs: 800 })
    } catch (error) {
      const message = getPaymentErrorMessage(error)
      setResumeErrorMessage(message)
      showToast('error', message)
      throw error
    }
  }

  const handlePlanSelect = planId => {
    setSelectedPlanId(planId)
    setErrorMessage('')
  }

  useEffect(() => {
    if (!isOpen) {
      setShowAllPlans(false)
      setShowResumePlanModal(false)
      setShowResumeAddCardModal(false)
      setResumeErrorMessage('')
      setResumeSelectedCardId('')
      setIsSavingResumeCard(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (!filteredVisibleCards.length && !pendingAddedCardId) {
      if (selectedCardId) {
        setSelectedCardId('')
      }
      return
    }

    const selectedCardStillExists =
      filteredVisibleCards.some(card => String(card.id) === String(selectedCardId)) ||
      pendingAddedCardId === String(selectedCardId)

    if (!selectedCardStillExists) {
      if (preferredPlanChangeCardId) {
        setSelectedCardId(preferredPlanChangeCardId)
      }
    }
  }, [filteredVisibleCards, pendingAddedCardId, preferredPlanChangeCardId, selectedCardId])

  useEffect(() => {
    if (!showResumePlanModal) return

    if (!resumeVisibleCards.length) {
      if (resumeSelectedCardId) {
        setResumeSelectedCardId('')
      }
      return
    }

    const resumeCardStillExists = resumeVisibleCards.some(
      card => String(card.id) === String(resumeSelectedCardId)
    )

    if (!resumeCardStillExists) {
      setResumeSelectedCardId(String(resumeVisibleCards[0].id))
    }
  }, [resumeSelectedCardId, resumeVisibleCards, showResumePlanModal])

  useEffect(() => {
    if (!resumePendingAddedCardId) return

    const pendingCardAlreadyExists = cards.some(
      card => String(card.id) === String(resumePendingAddedCardId)
    )

    if (pendingCardAlreadyExists) {
      setResumePendingAddedCard(null)
    }
  }, [cards, resumePendingAddedCardId])

  useEffect(() => {
    if (!changePlanModalOpen) {
      setErrorMessage('')
      setChangePlanStep(1)
      setSubscriptionId(null)
      setDidSkipCardSelection(false)
      setActiveChangePlanSlide(CHANGE_PLAN_SLIDES.plan)
      setIsSavingCard(false)
      changePlanSwiperRef.current = null
      return
    }

    requestAnimationFrame(() => {
      getChangePlanSwiper()?.slideTo(CHANGE_PLAN_SLIDES.plan, 0)
      setActiveChangePlanSlide(CHANGE_PLAN_SLIDES.plan)
    })
  }, [changePlanModalOpen])

  const handleConfirmPlanSelection = () => {
    if (!selectedPlanId) {
      setErrorMessage('Debes seleccionar un plan')
      return
    }

    if (
      selectedPlan &&
      currentPlan &&
      selectedPlan.ufPriceWithoutTaxes != null &&
      currentPlan.ufPriceWithoutTaxes != null &&
      selectedPlan.ufPriceWithoutTaxes < currentPlan.ufPriceWithoutTaxes
    ) {
      setShowPlanReductionModal(true)
      return
    }

    setErrorMessage('')

    if (preferredPlanChangeCardId) {
      if (selectedCardId !== preferredPlanChangeCardId) {
        setSelectedCardId(preferredPlanChangeCardId)
      }

      setDidSkipCardSelection(true)
      goToChangePlanSlide(CHANGE_PLAN_SLIDES.confirm)
      return
    }

    setDidSkipCardSelection(false)
    goToChangePlanSlide(CHANGE_PLAN_SLIDES.card)
  }

  const handleContinueFromCardSelection = () => {
    if (!selectedCardId) {
      setErrorMessage('Selecciona una tarjeta o agrega una nueva para continuar')
      return
    }

    setErrorMessage('')
    setDidSkipCardSelection(false)
    goToChangePlanSlide(CHANGE_PLAN_SLIDES.confirm)
  }

  const handleAddCardSuccess = card => {
    const nextCardId = card?.id != null ? String(card.id) : ''

    if (nextCardId) {
      setPendingAddedCard(card)
      setSelectedCardId(nextCardId)
    }

    showToast('success', 'Tarjeta registrada correctamente')
    setErrorMessage('')
    setDidSkipCardSelection(false)
    goToChangePlanSlide(CHANGE_PLAN_SLIDES.confirm)
  }

  const handlePaymentTokenSuccess = async ({ token, payerEmail }) => {
    if (!token) {
      showToast('error', 'No se pudo generar el token de pago')
      return
    }

    if (!selectedPlanId || !organizationId || !selectedCardId) {
      showToast('error', 'Faltan datos para actualizar el plan')
      return
    }

    try {
      if (changePlanStep === 1) {
        const { subscriptionId: nextSubscriptionId } = await createPlanSubscription.mutateAsync({
          organizationId,
          planData: {
            planId: selectedPlanId,
            cardId: Number(selectedCardId),
            token,
            payerEmail
          }
        })

        setSubscriptionId(nextSubscriptionId)
        setChangePlanStep(2)
        setErrorMessage('')
      } else {
        const result = await confirmPlanSubscription.mutateAsync({
          organizationId,
          subscriptionId,
          token
        })

        if (!isModal) {
          setShowChangePlanModal(false)
        }

        if (result?.paymentPendingReviewManual) {
          setShowPaymentPendingReviewModal(true)
        } else {
          setShowPlanChangeSuccessModal(true)
        }
      }
    } catch (error) {
      const message = getPaymentErrorMessage(error)
      setErrorMessage(message)
      showToast('error', message)
      throw error
    }
  }

  const handleCancelConfirmModal = () => {
    if (isPending) return

    setErrorMessage('')
    setChangePlanStep(1)
    setSubscriptionId(null)
    goToChangePlanSlide(
      didSkipCardSelection ? CHANGE_PLAN_SLIDES.plan : CHANGE_PLAN_SLIDES.card
    )
  }

  const handleClosePlanChangeSuccessModal = () => {
    setShowPlanChangeSuccessModal(false)
    resetPlanFlowState()
    handleTryLucaPlanRefresh(onPlanUpdated)
    if (isModal) onClose?.()
  }

  const handleClosePaymentPendingReviewModal = () => {
    setShowPaymentPendingReviewModal(false)
    resetPlanFlowState()
    if (isModal) onClose?.()
  }

  const handleCloseCancelPlanSuccessModal = () => {
    setShowCancelPlanSuccessModal(false)
    handleTryLucaPlanRefresh(onPlanUpdated)
  }

  const handleCancelChangePlanModal = () => {
    if (isFlowBusy) return

    resetPlanFlowState()

    if (isModal) {
      onClose?.()
    }
  }

  const handleFrequencyChange = (event, newValue) => {
    const frequency = newValue || event
    setSelectedFrequency(frequency)
    setSelectedPlanId(null)
    setErrorMessage('')
  }

  const handleOpenAddons = () => {
    resetPlanFlowState()
    setShowAddonsModal(true)

    if (isModal) {
      onClose?.()
    }
  }

  const handleAddonsSuccess = () => {
    setShowAddonsModal(false)
    if (onPlanUpdated) onPlanUpdated()
  }

  const handleCancelPlan = async () => {
    if (!organizationId) return

    try {
      await cancelOrganizationPlan.mutateAsync(organizationId)
      setShowCancelPlanModal(false)
      setShowCancelPlanSuccessModal(true)
    } catch (cancelError) {
      showToast('error', cancelError.response?.data?.message || 'Error al cancelar el plan')
    }
  }

  const confirmContent = (
    <PlanChangeConfirmContent
      selectedPlan={selectedPlan}
      ufValue={ufValue}
      changePlanStep={changePlanStep}
      onPaymentSuccess={handlePaymentTokenSuccess}
      onPaymentLayoutChange={updateChangePlanSwiperLayout}
      isPending={isPending}
      customerId={customerId}
      cardsIds={selectedPlanPaymentCardsIds}
      selectedCard={selectedPaymentCard}
      cardsLoading={cardsLoading}
      cards={filteredVisibleCards}
      selectedCardId={selectedCardId}
      onSelectCard={cardId => {
        setSelectedCardId(cardId)
        setErrorMessage('')
      }}
      cardSelectionStepSkipped={didSkipCardSelection}
      isPendingNewCardConfirmation={isPendingAddedCardSelected}
      errorMessage={errorMessage}
      hasCurrentPlan={hasCurrentPlan}
      paymentTypeId={selectedPlanPaymentTypeId}
    />
  )

  const selectionContent = (
    <PlanSelectionContent
      selectedFrequency={selectedFrequency}
      onFrequencyChange={handleFrequencyChange}
      availablePlans={availablePlans}
      selectedPlanId={selectedPlanId}
      onPlanSelect={handlePlanSelect}
      ufValue={ufValue}
      errorMessage={errorMessage}
      onLayoutChange={updateChangePlanSwiperLayout}
      showFilteredPlans={showFilteredPlans}
      onShowAllPlans={() => setShowAllPlans(true)}
    />
  )

  const addCardContent = (
    <div className='relative rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm'>
        {isSavingCard && (
          <div className='absolute inset-0 z-50 flex flex-col items-center justify-center rounded-[24px] bg-white/90 backdrop-blur-sm'>
            <div className='mb-4 h-16 w-16 animate-spin rounded-full border-b-4 border-t-4 border-accent1' />
            <h3 className='mb-2 text-xl font-sans font-bold text-slate-900'>Validando tarjeta</h3>
            <p className='text-center font-sans text-slate-600'>
              Espera mientras registramos la tarjeta para continuar con el plan.
            </p>
          </div>
        )}

        <PaymentForm
          amount={VALIDATION_AMOUNT}
          saveCard
          managedByParent
          flowType='change_card'
          brickId='changePlanAddCard'
          paymentTypeId={selectedPlanPaymentTypeId}
          successMessage='Tarjeta registrada correctamente.'
          //securityMessage='Se realizara un cobro de $50 que sera devuelto automaticamente para validar tu tarjeta.'
          onProcessingChange={setIsSavingCard}
          onLayoutChange={updateChangePlanSwiperLayout}
          onSuccess={handleAddCardSuccess}
        />
    </div>
  )

  const cardSelectionContent = (
      <PlanCardSelectionContent
      cardsLoading={cardsLoading}
      cards={filteredVisibleCards}
      selectedCardId={selectedCardId}
      onSelectCard={cardId => {
        setSelectedCardId(cardId)
        setErrorMessage('')
      }}
      selectedPlan={selectedPlan}
      addCardContent={addCardContent}
      pendingCardMessage={
        isPendingAddedCardSelected
          ? 'La tarjeta nueva se usara en la confirmacion final del plan y aparecera en la lista cuando completes ese paso.'
          : ''
      }
      errorMessage={activeChangePlanSlide === CHANGE_PLAN_SLIDES.card ? errorMessage : ''}
    />
  )

  const resumePlanContent = (
    <div className='space-y-6 py-4'>
      <div className='rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm'>
        <ResumePlanConfirmContent
          currentPlan={currentPlan}
          ufValue={ufValue}
          cards={resumeVisibleCards}
          selectedCardId={resumeSelectedCardId}
          onSelectedCardChange={setResumeSelectedCardId}
          customerId={customerId}
          cardsIds={resumePaymentCardsIds}
          onPaymentSuccess={handleResumePaymentSuccess}
          isPending={isResumePending}
          errorMessage={resumeErrorMessage}
        />
      </div>
    </div>
  )

  const selectionFooter = (
    <>
      <CustomButton
        title='Cancelar'
        color='primary'
        className='rounded-full px-4 py-2'
        onClick={handleCancelChangePlanModal}
        disabled={isFlowBusy}
      />
      {showAddonsButton && canContractAddons && (
        <CustomButton
          title='Contratar Add-on'
          color='accent2'
          className='rounded-full px-4 py-2'
          onClick={handleOpenAddons}
          disabled={isFlowBusy}
        />
      )}
      <CustomButton
        title='Continuar'
        color='accent2'
        className='rounded-full px-4 py-2'
        onClick={handleConfirmPlanSelection}
        disabled={!selectedPlanId || isFlowBusy}
      />
    </>
  )

  const cardSelectionFooter = (
    <>
      <CustomButton
        title='Volver'
        color='primary'
        className='rounded-full px-4 py-2'
        onClick={() => goToChangePlanSlide(CHANGE_PLAN_SLIDES.plan)}
        disabled={isFlowBusy}
      />
      <CustomButton
        title='Continuar'
        color='accent2'
        className='rounded-full px-4 py-2'
        onClick={handleContinueFromCardSelection}
        disabled={!selectedCardId || cardsLoading || isFlowBusy}
      />
    </>
  )

  const confirmFooter = (
    <CustomButton
      title='Volver'
      color='primary'
      className='rounded-full px-4 py-2'
      onClick={handleCancelConfirmModal}
      disabled={isPending}
    />
  )

  const resumePlanFooter = (
      <CustomButton
        title='Volver'
        color='primary'
        className='rounded-full px-4 py-2'
        onClick={handleCloseResumeModal}
        disabled={isResumePending || isSavingResumeCard}
      />
  )

  const modalFooterBySlide = {
    [CHANGE_PLAN_SLIDES.plan]: selectionFooter,
    [CHANGE_PLAN_SLIDES.card]: cardSelectionFooter,
    [CHANGE_PLAN_SLIDES.confirm]: confirmFooter
  }

  const changePlanModal = (
    <ResizableModal
      open={changePlanModalOpen}
      onClose={handleCancelChangePlanModal}
      size='large'
      footer={modalFooterBySlide[activeChangePlanSlide]}
    >
      <div className='flex h-full min-h-0 flex-col'>
        <div className='border-b border-gray-200 pb-4'>
          <div className='flex flex-col items-center gap-3 text-center'>
            <div className='w-full max-w-3xl'>
              <div className='flex w-full items-center justify-center rounded-full border border-primary/10 bg-primary/5 px-4 py-2 transition-colors duration-200'>
                <span className='text-sm font-sans font-semibold text-center text-primary sm:text-base'>
                  Paso {currentChangePlanStep} de {totalChangePlanSteps} - {activeSlideMeta.title}
                </span>
              </div>
            </div>

            <div className='max-w-2xl'>
              <p className='mt-1 font-sans text-sm text-gray-600'>
                {activeSlideMeta.description}
              </p>
            </div>
          </div>
        </div>

        <div className='flex-1 min-h-0 pt-4'>
          <Swiper
            allowTouchMove={false}
            observer
            observeParents
            onSwiper={swiper => {
              changePlanSwiperRef.current = swiper
              updateChangePlanSwiperLayout()
            }}
            onSlideChange={swiper => {
              setActiveChangePlanSlide(swiper.activeIndex)
              setErrorMessage('')
              requestAnimationFrame(() => {
                if (!swiper || swiper.destroyed || !swiper.params) return

                swiper.updateAutoHeight?.(0)
                swiper.update?.()
              })
            }}
            className='h-full min-h-0'
          >
            <SwiperSlide className='h-full'>
              <div className='h-full overflow-y-auto pr-1'>{selectionContent}</div>
            </SwiperSlide>

            <SwiperSlide className='h-full'>
              <div className='h-full overflow-y-auto pr-1'>{cardSelectionContent}</div>
            </SwiperSlide>

            <SwiperSlide className='h-full'>
              <div className='h-full overflow-y-auto pr-1'>{confirmContent}</div>
            </SwiperSlide>
          </Swiper>
        </div>
      </div>
    </ResizableModal>
  )

  const resumePlanModal = (
    <ResizableModal
      open={showResumePlanModal}
      onClose={handleCloseResumeModal}
      size='default'
      width={920}
      minWidth={760}
      minHeight={560}
      footer={resumePlanFooter}
    >
      <div className='flex h-full min-h-0 flex-col'>
        <div className='border-b border-gray-200 pb-4'>
          <h3 className='text-2xl font-sans font-bold text-gray-800'>Retomar plan</h3>
          <p className='mt-1 text-sm font-sans text-gray-600'>
            Reactiva el débito recurrente del plan actual usando una tarjeta guardada.
          </p>
        </div>

        <div className='flex-1 min-h-0 overflow-y-auto pt-4 pr-1'>{resumePlanContent}</div>
      </div>
    </ResizableModal>
  )


  const resumeAddCardModal = (
    <Modal isOpen={showResumeAddCardModal} onClose={handleCloseResumeAddCardModal}>
      <AddCardPaymentForm
        amount={VALIDATION_AMOUNT}
        onClose={handleCloseResumeAddCardModal}
        onSuccess={handleResumeAddCardSuccess}
        onProcessingChange={setIsSavingResumeCard}
        managedByParent
        isProcessing={isSavingResumeCard}
        paymentTypeId={resumePlanPaymentTypeId}
        flowType='plan_resume_add_card'
        brickId='resumePlanAddCard'
        successMessage='Tarjeta registrada correctamente.'
      />
    </Modal>
  )

  if (isLoading) {
    if (isModal) {
      return (
        <ResizableModal open={isOpen} onClose={handleCancelChangePlanModal} size='compact'>
          <div className='flex h-full items-center justify-center py-12'>
            <div className='h-12 w-12 animate-spin rounded-full border-b-4 border-t-4 border-accent1' />
          </div>
        </ResizableModal>
      )
    }

    return (
      <div className='flex items-center justify-center py-12'>
        <div className='h-12 w-12 animate-spin rounded-full border-b-4 border-t-4 border-accent1' />
      </div>
    )
  }

  if (isModal) {
    return (
      <>
        {changePlanModal}

        {resumePlanModal}
        {resumeAddCardModal}

        <ContractAddonsModal
          isOpen={showAddonsModal}
          onClose={() => setShowAddonsModal(false)}
          onSuccess={handleAddonsSuccess}
        />

        <PlanReductionModal
          isOpen={showPlanReductionModal}
          onClose={() => setShowPlanReductionModal(false)}
        />

        <Modal isOpen={showPlanChangeSuccessModal} onClose={handleClosePlanChangeSuccessModal}>
          <div className='w-full max-w-md rounded-lg bg-white p-8 text-center shadow-xl'>
            <div className='mx-auto mb-4 flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-green-100'>
              <svg
                className='h-8 w-8 text-green-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M5 13l4 4L19 7'
                />
              </svg>
            </div>
            <h3 className='mb-2 text-xl font-sans font-bold text-gray-900'>{successModalTitle}</h3>
            <p className='mb-6 text-sm font-sans text-gray-600'>{successModalDescription}</p>
            <CustomButton
              title='Entendido'
              color='primary'
              className='rounded-full px-6 py-2'
              onClick={handleClosePlanChangeSuccessModal}
            />
          </div>
        </Modal>

        <Modal isOpen={showCancelPlanSuccessModal} onClose={handleCloseCancelPlanSuccessModal}>
          <div className='w-full max-w-md rounded-lg bg-white p-8 text-center shadow-xl'>
            <div className='mx-auto mb-4 flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-green-100'>
              <svg
                className='h-8 w-8 text-green-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M5 13l4 4L19 7'
                />
              </svg>
            </div>
            <h3 className='mb-2 text-xl font-sans font-bold text-gray-900'>
              Plan cancelado correctamente
            </h3>
            <p className='mb-6 text-sm font-sans text-gray-600'>
              Tu plan fue cancelado correctamente.
            </p>
            <CustomButton
              title='Entendido'
              color='primary'
              className='rounded-full px-6 py-2'
              onClick={handleCloseCancelPlanSuccessModal}
            />
          </div>
        </Modal>

        <Modal
          isOpen={showPaymentPendingReviewModal}
          onClose={handleClosePaymentPendingReviewModal}
        >
          <div className='w-full max-w-md rounded-lg bg-white p-8 text-center shadow-xl'>
            <div className='mx-auto mb-4 flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-amber-100'>
              <svg
                className='h-8 w-8 text-amber-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
            </div>
            <h3 className='mb-2 text-xl font-sans font-bold text-gray-900'>Pago en revision</h3>
            <p className='mb-6 font-sans text-sm text-gray-600'>
              Tu pago esta en revision. Te avisaremos cuando sea confirmado.
            </p>
            <CustomButton
              title='Entendido'
              color='primary'
              className='rounded-full px-6 py-2'
              onClick={handleClosePaymentPendingReviewModal}
            />
          </div>
        </Modal>
      </>
    )
  }
  
  return (
    <div className='space-y-6'>
      <div className='grid grid-cols-1 gap-6 lg:grid-cols-3'>
        <CurrentPlanSection
          currentPlan={currentPlan}
          ufValue={ufValue}
          onChangePlanClick={handleChangePlanClick}
          onCancelPlanClick={planIsCancelled ? undefined : () => setShowCancelPlanModal(true)}
          onResumePlanClick={planIsCancelled ? handleResumePlanClick : undefined}
        />

        <div className='rounded-lg border border-gray-200 bg-white p-6 shadow-sm lg:col-span-2'>
          <SubscriptionHistoryTable subscriptions={subscriptions} />
        </div>
      </div>

      {currentPlan && !planIsCancelled && (
        <div className='relative overflow-hidden rounded-[28px] border border-slate-200 p-6 shadow-sm'>
          <div className='absolute inset-x-0 top-0 h-1.5' />
          <div className='mb-4 flex flex-wrap items-center justify-between gap-4'>
            <h3 className='text-lg font-sans font-semibold text-gray-800'>Addons contratados</h3>
            {canContractAddons && (
              <CustomButton
                title='Contratar addon'
                color='accent2'
                className='rounded-full px-4 py-2'
                onClick={() => setShowAddonsModal(true)}
              />
            )}
          </div>

          {activeAddons.length > 0 ? (
            <div className='rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm'>
              <div className='mb-4 flex items-center justify-between'>
                <span className='text-xs font-sans text-slate-400'>
                  {activeAddonsSummary.length} items
                </span>
              </div>

              <div className='divide-y divide-gray-100'>
                {activeAddonsSummary.map(addon => (
                  <div
                    key={addon.addonId}
                    className='flex items-center justify-between py-3'
                  >
                    <div className='min-w-0 flex flex-col'>
                      <span className='truncate text-sm font-sans font-medium text-gray-800'>
                        {addon.name}
                      </span>
                      <span className='mt-1 text-xs font-sans text-slate-500'>
                        {addon.unitUf != null
                          ? addon.quantity > 1
                            ? `${addon.totalUf.toFixed(2)} UF total + IVA / mes`
                            : `${Number(addon.unitUf).toFixed(2)} UF + IVA / mes`
                          : 'Precio a convenir'}
                      </span>
                    </div>

                    <div className='flex shrink-0 items-center gap-2'>
                      <div className='flex h-10 w-10 items-center justify-center rounded-full bg-accent2 text-sm font-sans font-semibold text-white'>
                        {addon.quantity}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/*<div className='mt-5 rounded-[20px] border border-accent2 bg-accent2/5 px-4 py-4'>
                <div className='flex justify-between gap-4'>
                  <span className='text-sm text-slate-600'>Total mensual</span>
                  <div className='text-right'>
                    <p className='text-xl font-sans font-semibold text-slate-900'>
                      {activeAddonsTotalUf.toFixed(2)} UF + IVA
                    </p>
                  </div>
                </div>
              </div>*/}
            </div>
          ) : (
            <p className='font-sans text-sm text-gray-500'>No tienes addons contratados.</p>
          )}
        </div>
      )}

      {resumePlanModal}
      {resumeAddCardModal}

      <ContractAddonsModal
        isOpen={showAddonsModal}
        onClose={() => setShowAddonsModal(false)}
        onSuccess={handleAddonsSuccess}
      />

      {changePlanModal}

      <PlanReductionModal
        isOpen={showPlanReductionModal}
        onClose={() => setShowPlanReductionModal(false)}
      />

      <CancelPlanModal
        isOpen={showCancelPlanModal}
        onClose={() => setShowCancelPlanModal(false)}
        onConfirm={handleCancelPlan}
        isPending={cancelOrganizationPlan.isPending}
      />

      <Modal isOpen={showPlanChangeSuccessModal} onClose={handleClosePlanChangeSuccessModal}>
        <div className='w-full max-w-md rounded-lg bg-white p-8 text-center shadow-xl'>
          <div className='mx-auto mb-4 flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-green-100'>
            <svg
              className='h-8 w-8 text-green-600'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M5 13l4 4L19 7'
              />
            </svg>
          </div>
          <h3 className='mb-2 text-xl font-sans font-bold text-gray-900'>{successModalTitle}</h3>
          <p className='mb-6 text-sm font-sans text-gray-600'>{successModalDescription}</p>
          <CustomButton
            title='Entendido'
            color='primary'
            className='rounded-full px-6 py-2'
            onClick={handleClosePlanChangeSuccessModal}
          />
        </div>
      </Modal>

      <Modal isOpen={showCancelPlanSuccessModal} onClose={handleCloseCancelPlanSuccessModal}>
        <div className='w-full max-w-md rounded-lg bg-white p-8 text-center shadow-xl'>
          <div className='mx-auto mb-4 flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-green-100'>
            <svg
              className='h-8 w-8 text-green-600'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M5 13l4 4L19 7'
              />
            </svg>
          </div>
          <h3 className='mb-2 text-xl font-sans font-bold text-gray-900'>
            Plan cancelado correctamente
          </h3>
          <p className='mb-6 text-sm font-sans text-gray-600'>
            Tu plan fue cancelado correctamente.
          </p>
          <CustomButton
            title='Entendido'
            color='primary'
            className='rounded-full px-6 py-2'
            onClick={handleCloseCancelPlanSuccessModal}
          />
        </div>
      </Modal>

      <Modal isOpen={showPaymentPendingReviewModal} onClose={handleClosePaymentPendingReviewModal}>
        <div className='w-full max-w-md rounded-lg bg-white p-8 text-center shadow-xl'>
          <div className='mx-auto mb-4 flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-amber-100'>
            <svg
              className='h-8 w-8 text-amber-600'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          </div>
          <h3 className='mb-2 text-xl font-sans font-bold text-gray-900'>Pago en revision</h3>
          <p className='mb-6 text-sm font-sans text-gray-600'>
            Tu pago esta en revision. Te avisaremos cuando sea confirmado.
          </p>
          <CustomButton
            title='Entendido'
            color='primary'
            className='rounded-full px-6 py-2'
            onClick={handleClosePaymentPendingReviewModal}
          />
        </div>
      </Modal>
    </div>
  )
}

export default PlansManager
